import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import VideoModal from '../../../components/VideoModal';
import '../mobileHero.css';
import ScoutDeskSection from './sections/ScoutDeskSection';
import HowItWorksCampusSection from './sections/HowItWorksCampusSection';
import FoundersSection from './sections/FoundersSection';
import MobileTestimonialsSection from './sections/MobileTestimonialsSection';
import MobilePricingSection from './sections/MobilePricingSection';
import MobileInfoPanel from '../../../components/mobile/MobileInfoPanel';

// Hotspot positioning for numbered pins on screenshot (same as desktop)
const HOTSPOTS = [
  {
    num: 1,
    box: { x: 5.5, y: 7, w: 17, h: 90 },
    pin: { x: 7.5, y: 9 }
  },
  {
    num: 2,
    box: { x: 28, y: 27, w: 47, h: 70 },
    pin: { x: 30, y: 29 }
  },
  {
    num: 3,
    box: { x: 78.5, y: 7, w: 20.5, h: 90 },
    pin: { x: 78.5, y: 30 }
  },
  {
    num: 4,
    box: { x: 27, y: 8, w: 48, h: 18 },
    pin: { x: 29, y: 10 }
  }
];

// Feature content for carousel (same as desktop)
const FEATURES = [
  {
    num: 1,
    title: "Stop wasting brainpower figuring out what class needs what",
    bullets: [
      "Open any course and see syllabus, documents, assignments, and deadlines in one place",
      "No switching tabs, no digging through emails, no guessing"
    ]
  },
  {
    num: 2,
    title: "Your semester gets built for you",
    bullets: [
      "Assignments and deadlines pulled in and laid out automatically",
      "You always know what's due, what's next, and what can wait",
      "No manual planning required"
    ]
  },
  {
    num: 3,
    title: "Study from your actual coursework, not generic AI junk",
    bullets: [
      "Quizzes, flashcards, podcasts, and guidance come from your uploaded files",
      "Nothing generic. Everything adapts to the material your professor assigned",
      "You study what's on the test, not random summaries"
    ]
  },
  {
    num: 4,
    title: "Know what's done, what's due, and what can wait",
    bullets: [
      "Levels, trophies, and completion tracking turn effort into visible wins",
      "Momentum stays clear across every class",
      "You build consistency without thinking about it"
    ]
  }
];

export default function MobileLandingPageVertical() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Fix viewport height for iOS Safari - prevents white gaps and jumps
  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty("--vh", `${window.innerHeight * 0.01}px`);
    };

    setVh();
    window.addEventListener("resize", setVh);
    window.addEventListener("orientationchange", setVh);
    window.addEventListener("pageshow", setVh); // iOS BFCache restore

    return () => {
      window.removeEventListener("resize", setVh);
      window.removeEventListener("orientationchange", setVh);
      window.removeEventListener("pageshow", setVh);
    };
  }, []);

  // Auto-advance carousel every 4.5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeatureIndex(prev => (prev + 1) % FEATURES.length);
    }, 4500);

    return () => clearInterval(timer);
  }, []);

  // Swipe detection
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      setActiveFeatureIndex(prev => (prev + 1) % FEATURES.length);
    } else if (isRightSwipe) {
      setActiveFeatureIndex(prev => (prev - 1 + FEATURES.length) % FEATURES.length);
    }
  };

  return (
    <div className="mobile-landing-clean">
      {/* Hero Section */}
      <section 
        className="mobileHero" 
        id="hero"
      >
        <div className="mobileHeroBg" style={{
          backgroundImage: 'url(/landing_page_hero/campus.png)'
        }}>
          {/* Overlay A: Soft top spotlight */}
          <div className="bgOverlaySpotlight" />
          {/* Overlay B: Lighter bottom readability fade */}
          <div className="bgOverlayFade" />

          <div className="mobileHero__content">
            {/* Scout on premium stage badge */}
            <div className="scoutStage">
              <img 
                src="/landing_page_hero/Scout_Thumbsup.png" 
                alt="Scout the Beagle companion" 
                className="scoutImg" 
              />
            </div>

            {/* Premium hero card */}
              <div className="mobileHeroCard" style={{ paddingBottom: '24px' }}>
              <h1 className="mobileHero__headline-gradient">
                Built for students with jobs, commutes, <span className="headline-emphasis">and real lives.</span>
              </h1>
              <div className="mobileHero__subhead-wrap">
                <p className="mobileHero__subheadline">
                  DocuHounds turns your syllabus, assignments, deadlines, and study materials into one system so you can keep up, even when life is packed.
                </p>
              </div>

              <ul className="mobileHero__value-bullets">
                <li>
                  <span className="bullet-check" aria-hidden="true">✓</span>
                  See what's due across every class without digging through five apps
                </li>
                <li>
                  <span className="bullet-check" aria-hidden="true">✓</span>
                  Turn uploaded course material into quizzes, flashcards, podcasts, and audiobooks
                </li>
                <li>
                  <span className="bullet-check" aria-hidden="true">✓</span>
                  Stay on track when you're working, commuting, or catching up late at night
                </li>
              </ul>

              <div className="heroCtas">
                <Link to="/m/register" className="ctaPrimary">
                  START FREE
                </Link>
                <button
                  type="button"
                  className="ctaSecondary"
                  onClick={() => setIsVideoOpen(true)}
                  aria-label="See how it works"
                >
                  SEE HOW IT WORKS
                </button>
              </div>
              <p className="heroFinePrint">
                One system for school. Built for students balancing real life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* System Overview Section - Mobile */}
      <section className="mobileSystemOverview">
        {/* Section Header */}
        <div className="systemOverview__header">
          <h2 className="systemOverview__title">
            Everything you need for all your classes in one place.
          </h2>
          <p className="systemOverview__subtitle">
            DocuHounds integrates your syllabus, assignments, deadlines, and study tools into one dashboard.
          </p>
        </div>

        {/* Dashboard Screenshot with Highlighted Numbers */}
        <div className="systemOverview__screenshotWrapper">
          <img
            src="/landing_page_hero/screenshots/screenshot1_dashboardmain.png"
            alt="DocuHounds dashboard showing timeline, courses, and study tools"
            className="systemOverview__screenshot"
          />
          
          {/* Dim overlay */}
          <div className="screenshotDim" />
          
          {/* Numbered pins and highlights */}
          {HOTSPOTS.map((hotspot, idx) => {
            const isActive = idx === activeFeatureIndex;
            
            return (
              <React.Fragment key={hotspot.num}>
                {/* Highlight box */}
                <div
                  className={`screenshotHighlight ${isActive ? 'screenshotHighlight--active' : ''} ${activeFeatureIndex !== null && !isActive ? 'screenshotHighlight--dimmed' : ''}`}
                  style={{
                    left: `${hotspot.box.x}%`,
                    top: `${hotspot.box.y}%`,
                    width: `${hotspot.box.w}%`,
                    height: `${hotspot.box.h}%`
                  }}
                />
                
                {/* Numbered pin */}
                <div
                  className={`screenshotPin ${isActive ? 'screenshotPin--active' : ''}`}
                  style={{
                    left: `${hotspot.pin.x}%`,
                    top: `${hotspot.pin.y}%`
                  }}
                >
                  {hotspot.num}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Feature Carousel - Single Card */}
        <div 
          className="featureCarousel"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="featureCarousel__track" style={{
            transform: `translateX(-${activeFeatureIndex * 100}%)`
          }}>
            {FEATURES.map((feature, idx) => (
              <div key={feature.num} className="featureCarousel__slide">
                <div className="featureCard">
                  <div className="featureCard__number">{feature.num}</div>
                  <h3 className="featureCard__title">{feature.title}</h3>
                  <ul className="featureCard__bullets">
                    {feature.bullets.map((bullet, bulletIdx) => (
                      <li key={bulletIdx}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="carouselDots">
          {FEATURES.map((_, idx) => (
            <button
              key={idx}
              className={`carouselDot ${idx === activeFeatureIndex ? 'carouselDot--active' : ''}`}
              onClick={() => setActiveFeatureIndex(idx)}
              aria-label={`Go to feature ${idx + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Scout's Desk Section - Interactive AI Tools */}
      <ScoutDeskSection />

      {/* How It Works Section - Campus Carousel */}
      <HowItWorksCampusSection />

      {/* Founders Section */}
      <FoundersSection />

      {/* Testimonials Section - Loved by students everywhere */}
      <MobileTestimonialsSection />

      {/* Pricing Section */}
      <MobilePricingSection />

      {/* Floating Info Panel */}
      <MobileInfoPanel />

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl="https://www.youtube.com/embed/tkjEytwGtec?autoplay=1"
      />
    </div>
  );
}
